import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { TShirtIcon } from '@phosphor-icons/react'

import ProductsCard from '@/metrics/components/ProductsCard/ProductsCard'
import { groupByValues } from '@/metrics/constants'
import { entityTypes } from '@/metrics/hooks/useMetrics/constants'
import useMetrics from '@/metrics/hooks/useMetrics/useMetrics'
import metricsServices from '@/metrics/services/services'
import routes from '@/Router/routes.constants'

import SubReasonsCard from '../SubReasonsCard/SubReasonsCard'
import { productsDescriptions } from './constants'
import * as Styles from './styles'

const ReasonMetrics = ({
  reasonKey,
  returnPortalId,
  commonFilters,
  salesOrdersDataActivated,
  isUnifiedDashboard,
  showExchanges,
  subReasonsOrdering,
  productsFilters,
  setProductsFilters,
}) => {
  const { t } = useTranslation()
  const navigation = useNavigate()

  const { data, loading: loadingReasonTopReturnedProducts } = useMetrics({
    returnPortalId,
    service: metricsServices.getReasonTopReturnedProducts,
    entityId: reasonKey,
    entityType: entityTypes.reason,
    filters: commonFilters,
    additionalFilters: {
      ordering: productsFilters?.ordering,
    },
  })

  const { data: subReasons, loading: loadingSubReasons } = useMetrics({
    returnPortalId,
    service: metricsServices.getTopSubReason,
    entityId: reasonKey,
    entityType: entityTypes.reason,
    filters: commonFilters,
    additionalFilters: {
      reasonId: reasonKey,
      ordering: subReasonsOrdering,
    },
    doRequest: !!subReasonsOrdering,
  })

  const handleClickProduct = productPosition => {
    const currentParams = new URLSearchParams(window.location.search)
    const pathName = isUnifiedDashboard
      ? routes.merchantDashboardProductByReasonMetricsDetailPage
      : routes.productByReasonMetricsDetailPage

    navigation({
      pathname: pathName.replace(':reasonKey', reasonKey).replace(':productPosition', productPosition),
      search: currentParams.toString(),
    })
  }

  const handleClickSubReason = subReasonId => {
    const currentParams = new URLSearchParams(window.location.search)
    const pathName = isUnifiedDashboard
      ? routes.merchantDashboardSubReasonMetricsDetailPage
      : routes.subReasonMetricsDetailPage

    navigation({
      pathname: pathName.replace(':reasonKey', reasonKey).replace(':subReasonId', subReasonId),
      search: currentParams.toString(),
    })
  }

  const getSubReasons = () => {
    return subReasons?.results?.map(subReason => ({
      ...subReason,
      display: subReason?.internal_name || t('Sin submotivo'),
    }))
  }

  const productsDescription = salesOrdersDataActivated
    ? productsDescriptions.withSales
    : productsDescriptions.withoutSales

  const getProductsTooltip = () => {
    if (!salesOrdersDataActivated) return null

    return t('Las unidades vendidas son las totales del producto en el periodo, no solo las devueltas por este motivo.')
  }

  const hasSubReasons = subReasons?.results?.length > 0

  return (
    <Styles.ColumnsContainer hasSubReasons={hasSubReasons}>
      {hasSubReasons && (
        <SubReasonsCard
          subReasons={getSubReasons()}
          loading={loadingSubReasons}
          showExchanges={showExchanges}
          orderBy={subReasonsOrdering}
          onClickSubReason={handleClickSubReason}
        />
      )}
      <ProductsCard
        Icon={TShirtIcon}
        title={t('Productos más devueltos')}
        description={t(productsDescription)}
        tooltip={getProductsTooltip()}
        rateLabel={t('% Tasa de devolución de productos')}
        showSearch={false}
        loading={loadingReasonTopReturnedProducts}
        products={data?.results}
        showGroupBySelector={false}
        showExport={false}
        productsFilters={{ groupBy: groupByValues.products, ordering: productsFilters?.ordering }}
        onChangeProductsFilters={setProductsFilters}
        onClickProduct={handleClickProduct}
        salesOrdersDataActivated={salesOrdersDataActivated}
        isUnifiedDashboard={isUnifiedDashboard}
      />
    </Styles.ColumnsContainer>
  )
}

export default ReasonMetrics
